import React, { useState, useEffect } from 'react';
import './App.css';
import { ToastProvider } from './components/Toast';
import Navbar from './components/Navbar';
import Auth from './components/Auth';
import Browse from './components/Browse';
import Watchlist from './components/Watchlist';
import Account from './components/Account';
import ResetPassword from './components/ResetPassword';
import PrivacyPolicy from './components/PrivacyPolicy';
import Terms from './components/Terms';
import Footer from './components/Footer';
import AdminDashboard from './components/AdminDashboard';
import Contact from './components/Contact';
import api from './services/api';

/**
 * Decode the JWT payload synchronously — no network call needed.
 * The signature is not verified here (server does that); we just read
 * the claims so the navbar can render the correct state on the first paint.
 * Returns null if the token is missing, malformed, or expired.
 */
function parseJWT(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    // Reject already-expired tokens so we don't flash a logged-in navbar
    if (payload.exp && payload.exp * 1000 < Date.now()) return null;
    return { id: payload.id, name: payload.name, email: payload.email };
  } catch {
    return null;
  }
}

function AppContent() {
  // Static route: /admin
  if (window.location.pathname === '/admin') {
    return <AdminDashboard />;
  }

  // Static route: /reset-password
  if (window.location.pathname === '/reset-password') {
    return (
      <ResetPassword onBackToLogin={() => {
        window.history.replaceState(null, '', '/');
        window.location.reload();
      }} />
    );
  }

  // Static route: /privacy-policy
  if (window.location.pathname === '/privacy-policy') {
    return (
      <PrivacyPolicy onBack={() => {
        window.history.replaceState(null, '', '/');
        window.location.reload();
      }} />
    );
  }

  // Static route: /terms
  if (window.location.pathname === '/terms') {
    return (
      <Terms onBack={() => {
        window.history.replaceState(null, '', '/');
        window.location.reload();
      }} />
    );
  }

  // Static route: /contact
  if (window.location.pathname === '/contact') {
    return (
      <Contact onBack={() => {
        window.history.replaceState(null, '', '/');
        window.location.reload();
      }} />
    );
  }

  // Static route: /signup — standalone signup page (shareable URL for A2P compliance)
  if (window.location.pathname === '/signup') {
    return (
      <>
        <Navbar
          activeTab={null}
          setActiveTab={() => { window.history.replaceState(null, '', '/'); window.location.reload(); }}
          user={null}
          onLogout={() => {}}
          onShowAuth={() => {}}
        />
        <main className="main-content">
          <Auth
            standalone={true}
            initialMode="signup"
            onAuth={() => {
              window.history.replaceState(null, '', '/');
              window.location.reload();
            }}
          />
        </main>
        <Footer />
      </>
    );
  }

  // Read auth state synchronously from the JWT before the first render.
  // This means the navbar always paints with the correct logged-in/out
  // state and never has to change after mount — zero flicker.
  const [user, setUser] = useState(() => {
    const token = api.getToken();
    return token ? parseJWT(token) : null;
  });

  const [activeTab, setActiveTab] = useState('coming_soon');
  const [watchlistIds, setWatchlistIds] = useState(new Set());
  const [authMode, setAuthMode] = useState(null); // null | 'login' | 'signup'

  // Background verification: silently confirm the token is still valid with
  // the server and refresh user data (e.g. phone). If invalid, sign out.
  // This never blocks the first render.
  useEffect(() => {
    if (!api.isLoggedIn()) return;
    api.getMe()
      .then(data => {
        setUser(data.user);
        loadWatchlistIds();
      })
      .catch(() => {
        api.logout();
        setUser(null);
      });
  }, []);

  async function loadWatchlistIds() {
    try {
      const data = await api.getWatchlist();
      const ids = new Set((data.watchlist || []).map(m => m.id));
      setWatchlistIds(ids);
    } catch {
      // non-critical
    }
  }

  function handleAuth(userData) {
    setUser(userData);
    setAuthMode(null);
    setActiveTab('coming_soon');
    loadWatchlistIds();
  }

  function handleLogout() {
    api.logout();
    setUser(null);
    setWatchlistIds(new Set());
    setActiveTab('coming_soon');
  }

  function handleWatchlistChange(movieId, added) {
    setWatchlistIds(prev => {
      const next = new Set(prev);
      if (added) next.add(movieId);
      else next.delete(movieId);
      return next;
    });
  }

  function handleShowAuth(mode) {
    setAuthMode(mode);
  }

  function handleTabChange(tab) {
    if (!user && (tab === 'watchlist' || tab === 'account')) {
      setAuthMode('login');
    } else {
      setActiveTab(tab);
      setAuthMode(null);
    }
    if (tab === 'coming_soon' || tab === 'now_showing') {
      setAuthMode(null);
    }
  }

  // Show auth form
  if (!user && authMode) {
    return (
      <>
        <Navbar
          activeTab={activeTab}
          setActiveTab={handleTabChange}
          user={null}
          onLogout={handleLogout}
          onShowAuth={handleShowAuth}
        />
        <Auth
          onAuth={handleAuth}
          initialMode={authMode}
          onClose={() => setAuthMode(null)}
        />
      </>
    );
  }

  function renderContent() {
    switch (activeTab) {
      case 'coming_soon':
      case 'now_showing':
        return (
          <Browse
            mode={activeTab}
            onModeChange={handleTabChange}
            user={user}
            watchlistIds={watchlistIds}
            onWatchlistChange={handleWatchlistChange}
            onShowAuth={handleShowAuth}
          />
        );
      case 'watchlist':
        return (
          <Watchlist
            user={user}
            onWatchlistChange={handleWatchlistChange}
            setActiveTab={setActiveTab}
          />
        );
      case 'account':
        return (
          <Account
            user={user}
            onLogout={handleLogout}
            setActiveTab={setActiveTab}
            onUserUpdate={setUser}
          />
        );
      default:
        return null;
    }
  }

  return (
    <>
      <Navbar
        activeTab={activeTab}
        setActiveTab={handleTabChange}
        user={user}
        onLogout={handleLogout}
        onShowAuth={handleShowAuth}
      />
      <main className="main-content">
        {renderContent()}
      </main>
      <Footer />
    </>
  );
}

export default function App() {
  return (
    <ToastProvider>
      <AppContent />
    </ToastProvider>
  );
}
